/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, Heading, HFlow, Icon, ModalCloseButton, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { AccordionInformation } from 'components/accordion/AccordionInformation'
import { MutableRefObject } from 'react'

import { MChatRfResultadoAlert } from '../../components/MChatRfResultadoAlert'
import { getAplicacaoStats } from '../consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { ConsultaSeguimentoResultadoTable } from '../consulta-seguimento-resultado/ConsultaSeguimentoResultadoTable'
import {
  ConsultaSeguimentoFieldModel,
  ConsultaSeguimentoResultadoEnum,
  ConsultaSeguimentoResultadoMap,
} from '../model-consultaSeguimento'
import { getResultadoLabel } from '../utils-consultaSeguimento'

interface ConsultaSeguimentoModalTabResultadoProps {
  value: ConsultaSeguimentoFieldModel
  backToAplicacaoButtonRef: MutableRefObject<HTMLButtonElement>
  onGoBackToAplicacao: () => void
}

export function ConsultaSeguimentoModalTabResultado(props: Readonly<ConsultaSeguimentoModalTabResultadoProps>) {
  const { value, backToAplicacaoButtonRef, onGoBackToAplicacao } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const { type, title, encaminhamento } = ConsultaSeguimentoResultadoMap[value.resultado]

  const aplicacaoStats = getAplicacaoStats(value.aplicacao)

  return (
    <VFlow>
      <VFlow vSpacing={2}>
        <HFlow alignItems='flex-start' justifyContent='space-between'>
          <Button kind='normal' size='small' innerRef={backToAplicacaoButtonRef} onClick={onGoBackToAplicacao}>
            <Icon icon='arrowLeft' style={styles.iconMargin} />
            Voltar à avaliação
          </Button>
          <ModalCloseButton />
        </HFlow>
        <VFlow>
          <Heading level={1}>Resultado Entrevista de seguimento</Heading>
          <HFlow alignItems='flex-start' justifyContent='space-between'>
            <VFlow vSpacing={0.175}>
              <Heading
                level={2}
                color={value.resultado === ConsultaSeguimentoResultadoEnum.RASTREAMENTO_NEGATIVO ? 'success' : 'danger'}
              >
                {title}
              </Heading>
              <Text fontWeight='bold'>{getResultadoLabel(aplicacaoStats)}</Text>
            </VFlow>
            <Button kind='primary' skin='outline' size='small' style={styles.printButton}>
              <Icon icon='printerOutline' style={styles.iconMargin} />
              Imprimir resultado
            </Button>
          </HFlow>
          <AccordionInformation uuid='resultado' type='normal' header='Como é calculado o resultado?'>
            <Text>
              A entrevista é considerada como sendo de rastreamento positivo se a criança falhar em dois itens quaisquer
              da entrevista de seguimento. Se a criança for rastreada positivamente no M-CHAT-R/F, é altamente
              recomendado que a criança seja encaminhada para a intervenção precoce e testagem diagnóstica o mais breve
              possível. Atenção, se o agente de saúde ou o familiar tiver preocupações sobre TEA, a criança deve ser
              encaminhada para avaliação, independente da pontuação no M-CHAT-R ou M-CHAT-R/F.
            </Text>
          </AccordionInformation>
          <VFlow vSpacing={0.5}>
            <Text fontWeight='bold'>Encaminhamento</Text>
            <MChatRfResultadoAlert type={type}>{encaminhamento}</MChatRfResultadoAlert>
          </VFlow>
          <ConsultaSeguimentoResultadoTable aplicacao={value.aplicacao} />
        </VFlow>
      </VFlow>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  iconMargin: css`
    margin-right: 0.5rem;
  `,
  alert: {
    [ConsultaSeguimentoResultadoEnum.RASTREAMENTO_NEGATIVO]: css`
      color: ${theme.pallete.status.success.main};
      border: 1px solid ${theme.pallete.status.success.main};
      background: ${theme.pallete.status.success.background};
      padding: 0.5rem;
    `,
    [ConsultaSeguimentoResultadoEnum.RASTREAMENTO_POSITIVO]: css`
      color: ${theme.pallete.status.danger.main};
      border: 1px solid ${theme.pallete.status.danger.main};
      background: ${theme.pallete.status.danger.background};
      padding: 0.5rem;
    `,
  },
  printButton: css`
    margin-top: 0.25rem;
  `,
})
